import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Mail, Phone, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service: "",
  preferred_date: "",
  preferred_time: "",
  message: "",
};

export default function Appointment() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Please select a service");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      setSubmitted(true);
      toast.success("Appointment request received! We'll contact you within 24 hours.");
      const subject = encodeURIComponent(`Appointment Request — ${form.service} — ${form.name}`);
      const body = encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nCompany: ${form.company || "-"}\nService: ${form.service}\nPreferred Date: ${form.preferred_date}\nPreferred Time: ${form.preferred_time}\n\nMessage:\n${form.message || "-"}`
      );
      window.location.href = `mailto:info@coresure.us?subject=${subject}&body=${body}`;
    } catch (err) {
      toast.error("Something went wrong. Please try again or email info@coresure.us");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="appointment-page">
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5A00]">Appointment</span>
          <h1 className="font-heading font-extrabold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl">
            Book your <span className="text-[#FF5A00]">free</span> consultation.
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            15 minutes with our team. We'll assess your needs, recommend a plan, and set up your 1-week free trial.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3">
          <div className="px-4 sm:px-6 lg:px-8 py-16 bg-[#0A0A0A] text-white">
            <h2 className="font-heading font-extrabold text-2xl tracking-tight">Contact details</h2>
            <ul className="mt-8 space-y-6 text-sm">
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-[#FF5A00] mt-0.5" />
                <div>
                  <p className="font-bold">Email</p>
                  <a href="mailto:info@coresure.us" data-testid="appointment-email-link" className="text-zinc-400 mt-0.5 hover:text-[#FF5A00] transition-colors">info@coresure.us</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={18} className="text-[#FF5A00] mt-0.5" />
                <div>
                  <p className="font-bold">Phone / WhatsApp</p>
                  <a href="https://wa.me/919034950501" target="_blank" rel="noopener noreferrer" data-testid="appointment-whatsapp-link" className="text-zinc-400 mt-0.5 hover:text-[#FF5A00] transition-colors">+91-9034950501</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#FF5A00] mt-0.5" />
                <div><p className="font-bold">Coverage</p><p className="text-zinc-400 mt-0.5">All US states &amp; Canadian provinces</p></div>
              </li>
              <li className="flex items-start gap-3">
                <CalendarCheck size={18} className="text-[#FF5A00] mt-0.5" />
                <div><p className="font-bold">Hours</p><p className="text-zinc-400 mt-0.5">Mon–Fri, 8am–8pm ET</p></div>
              </li>
            </ul>
            <div className="mt-10 border border-[#27272A] p-6">
              <p className="font-mono-price text-2xl font-bold text-[#FF5A00]">1 Week Free</p>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">Every new client gets a full week of bookkeeping at no charge. No credit card required.</p>
            </div>
          </div>

          <div className="lg:col-span-2 px-4 sm:px-6 lg:px-12 py-16 lg:border-l border-border">
            {submitted ? (
              <div data-testid="appointment-success" className="border border-[#FF5A00] bg-[#FFF3ED] p-12 text-center">
                <CheckCircle2 size={48} className="text-[#FF5A00] mx-auto" />
                <h2 className="font-heading font-extrabold text-2xl mt-4">Request received!</h2>
                <p className="text-muted-foreground mt-3 max-w-md mx-auto leading-relaxed">
                  Thank you, {form.name.split(" ")[0]}. Our team will contact you at {form.email} within 24 hours to confirm your appointment and set up your free trial week. Your email app should open with the details pre-filled — just hit send to reach us at info@coresure.us.
                </p>
                <button
                  data-testid="appointment-book-another"
                  onClick={() => { setForm(initialForm); setSubmitted(false); }}
                  className="mt-6 border border-foreground font-bold px-6 py-3 text-sm hover:bg-foreground hover:text-white transition-colors"
                >
                  Book another appointment
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="appointment-form" className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full name *</Label>
                    <Input id="name" data-testid="appointment-input-name" required value={form.name} onChange={set("name")} placeholder="Jane Smith" className="rounded-none h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" data-testid="appointment-input-email" type="email" required value={form.email} onChange={set("email")} placeholder="jane@company.com" className="rounded-none h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input id="phone" data-testid="appointment-input-phone" required value={form.phone} onChange={set("phone")} placeholder="+1 (555) 000-0000" className="rounded-none h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Company</Label>
                    <Input id="company" data-testid="appointment-input-company" value={form.company} onChange={set("company")} placeholder="Acme Inc." className="rounded-none h-12" />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Service needed *</Label>
                    <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                      <SelectTrigger data-testid="appointment-select-service" className="rounded-none h-12">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="Bookkeeping">Bookkeeping</SelectItem>
                        <SelectItem value="Accounting">Accounting</SelectItem>
                        <SelectItem value="Accounts Payable">Accounts Payable</SelectItem>
                        <SelectItem value="Accounts Receivable">Accounts Receivable</SelectItem>
                        <SelectItem value="Multiple / Not sure">Multiple / Not sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date">Preferred date *</Label>
                    <Input id="date" data-testid="appointment-input-date" type="date" required value={form.preferred_date} onChange={set("preferred_date")} className="rounded-none h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="time">Preferred time *</Label>
                    <Input id="time" data-testid="appointment-input-time" type="time" required value={form.preferred_time} onChange={set("preferred_time")} className="rounded-none h-12" />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="message">Tell us about your business</Label>
                    <Textarea id="message" data-testid="appointment-input-message" value={form.message} onChange={set("message")} placeholder="Industry, monthly transaction volume, current bookkeeping setup..." rows={4} className="rounded-none" />
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="appointment-submit-btn"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] disabled:opacity-60 text-white font-bold px-10 py-4 transition-colors w-full sm:w-auto"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <CalendarCheck size={18} />}
                  {loading ? "Submitting..." : "Request Appointment"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
